import { snakeCase } from "lodash";

import { isDev, VITE_LEDGER_BASE_URL } from "../../env";
import request from "../request";
import {
  bugsnagGeneralErrorHandler,
  bugsnagPostgrestErrorHandler,
  formatDataObj,
  recursiveCamelCaseCipher,
  recursiveSnakeCaseCipher,
} from "./common";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  Building,
  SummaryOverview,
  SummaryOverviewQueryParams,
} from "../../types";

const outBoundSiteEncoder = {
  accountId: "account_id",
  cbecsPrincipleBuildingActivity: "cbecs_principle_building_activity",
  cbecsMoreSpecificPrincipleBuildingActivity:
    "cbecs_more_specific_principle_building_activity",
  name: "name",
  legalName: "legal_name",
  squareFeet: "square_feet",
  squareFeetRentable: "square_feet_rentable",
  numberOfFloors: "number_of_floors",
  numberOfBasements: "number_of_basements",
  numberOfUnits: "number_of_units",
  streetAddress: "street_address",
  productTier: "product_tier",
  zipcode: "zipcode",
  city: "city",
  state: "state",
  yearConstructed: "year_constructed",
  timeZone: "time_zone",
  weatherLatitude: "weather_latitude",
  weatherLongitude: "weather_longitude",
  energyStarPropertyType: "energy_star_property_type",
  siteOwnership: "site_ownership",
  yearCommissioned: "year_commissioned",
  expectedSaleDate: "expected_sale_date",
  utilityOptOut: "utility_opt_out",
  gresbOptOut: "gresb_opt_out",
  leedOptOut: "leed_opt_out",
  needsEnergystarAccount: "needs_energystar_account",
  market: "market",
  fund: "fund",
  onboardingCompletedAt: "onboarding_completed_at",
  isTestSite: "is_test_site",
} as const;

export function getImageUrlFromKey(imageKey?: string | null) {
  if (!imageKey) {
    return null;
  }
  return `https://bractlet-public${
    isDev ? "-dev" : ""
  }.s3.amazonaws.com/valinor/building_images/${imageKey}.jpeg`;
}

const formatInboundSite = (inboundSite: any): Building => {
  return {
    ...inboundSite,
    account: inboundSite?.accountName, // deprecated field kept for backwards compatibility
    imageURL: getImageUrlFromKey(inboundSite?.imageKey),
  };
};

export async function getSites<
  T extends SummaryOverviewQueryParams | void = void
>({
  includeInactive = false,
  accountId,
  fields,
}: {
  includeInactive?: boolean;
  accountId?: number;
  fields?: T;
} = {}): Promise<Array<T extends void ? Building : SummaryOverview>> {
  return await request
    .get(`${VITE_LEDGER_BASE_URL}/sites/summary`)
    .query({
      order: "name",
      ...(includeInactive && { status: "all" }),
      ...(accountId && { account_id: accountId }),
      ...(fields?.length && { fields: fields.map(snakeCase).join(",") }),
    })
    .catch((e) => {
      bugsnagPostgrestErrorHandler(e);
      return { body: [] };
    })
    .then(({ body }) => recursiveCamelCaseCipher(body))
    .then((r) => {
      return r.map(formatInboundSite);
    });
}

export async function getSite<
  T extends SummaryOverviewQueryParams | void = void
>(
  siteId: string | number,
  {
    includeInactive = false,
    fields,
    throwErrors = false,
  }: {
    includeInactive?: boolean;
    fields?: T;
    throwErrors?: boolean;
  } = {}
): Promise<(T extends void ? Building : SummaryOverview) | null> {
  try {
    const { body } = await request
      .get(`${VITE_LEDGER_BASE_URL}/sites/summary`)
      .query({
        site_id: siteId,
        ...(!includeInactive && { status: "active" }), // querying by site id with no status bypasses a status check in the service
        ...(fields?.length && { fields: fields.map(snakeCase).join(",") }),
      });

    const r = recursiveCamelCaseCipher(body[0]);
    if (!r) {
      return null;
    }
    return formatInboundSite(r);
  } catch (e) {
    if (throwErrors) {
      throw e;
    }
    bugsnagPostgrestErrorHandler(e);
    return null;
  }
}

export const updateSite = (
  siteId: string | number,
  props: Partial<Building>
): Promise<void> =>
  // @ts-expect-error - TS2322 - Type 'Promise<void | Response>' is not assignable to type 'Promise<void>'.
  request
    .patch(`/api/buildings`)
    .query({ id: `eq.${siteId}` })
    .send(
      formatDataObj(props, outBoundSiteEncoder, {
        allowNulls: true,
        allowUnknownKeys: false,
      })
    );

export const uploadBuildingImage = async (
  siteId: number,
  imageBlob: Blob
): Promise<void> =>
  // @ts-expect-error - TS2322 - Type 'void | Response' is not assignable to type 'void'.
  request
    .post("/uploads")
    .send({ type: "building_image", id: siteId })
    .then(({ body }) =>
      fetch(body.url, {
        // received pre-signed S3 url from backend to upload to S3 - use fetch to omit auth middleware
        method: "PUT",
        headers: {
          "Content-Type": imageBlob.type,
        },
        body: imageBlob,
      })
    )
    .then(async (res) => {
      if (res.ok) {
        return request
          .post("/upload_completions")
          .send({ type: "building_image", id: siteId });
      }
      throw new Error(await res.text());
    })
    .catch(bugsnagGeneralErrorHandler);

export const createNewSite = (newSite: Partial<Building>): Promise<Building> =>
  request
    .post("/api/buildings")
    .send(
      formatDataObj(newSite, outBoundSiteEncoder, {
        allowNulls: true,
        allowUnknownKeys: false,
      })
    )
    .then(({ body }) => formatInboundSite(body[0]));

export type SiteEventStatus = "todo" | "planned" | "completed" | "cancelled";
export type SiteEvent = {
  id: number;
  siteId: number;
  eventType: "gateway-installation" | "site-walk-through";
  subject: string;
  plannedAt: string | null; // date,
  realizedAt: string; //date,
  visitorUserId: number;
  hostUserId: number;
  notes: string;
  createdAt: string; //date,
  status: SiteEventStatus;
};
const getSiteEvents = (siteId: number): Promise<SiteEvent[]> =>
  request
    .get("/rest/v_site_events")
    .query({ site_id: `eq.${siteId}` })
    .then(({ body }) => recursiveCamelCaseCipher(body));

export const useGetSiteWalkthroughEvent = (
  siteId: number,
  { enabled = true }: { enabled?: boolean } = {}
) =>
  useQuery({
    queryKey: ["site-events", siteId],
    queryFn: () => getSiteEvents(siteId),
    enabled,
    select: (siteEvents) =>
      siteEvents.find((event) => event.eventType === "site-walk-through"),
  });

const patchSiteEvent = ({
  eventId,
  event,
}: {
  eventId: number;
  event: Partial<SiteEvent>;
}) =>
  request
    .patch("/rest/v_site_events")
    .query({ id: `eq.${eventId}` })
    .send({ ...recursiveSnakeCaseCipher(event) });

export const usePatchSiteEvent = (siteId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchSiteEvent,
    onSettled: async () => {
      return queryClient.invalidateQueries({
        queryKey: ["site-events", siteId],
      });
    },
  });
};
